// @givmo/employer-module — choosing what a person can open: a Global Admin, or exactly the apps
// ticked (EP-SUITE8, D-EP-69: Jonathan's D-CPO-64 and its 23:32 Also). PURE: no React, no I/O.
//
// Two acts take this choice, each on its own door (Org Machinery's OM-57, be#3523):
//   * approving a join request, `POST /admin/company/join-requests/{request_id}/approval`, whose
//     refusals are the `JOIN_APPROVAL_*` codes;
//   * changing an admin's role, `POST /admin/company/admins/{target_user_id}/role-change`, the
//     `ROLE_CHANGE_*` codes. No screen sends it: the old employer portal's Edit admin is prod's
//     Owner / Member choice on `PUT …/role` (D-EP-86, EP-WEB64), and the console changes roles on
//     its own organization route.
// Both take EXACTLY `{role, modules: [{module, role}]}` and nothing else (Org Machinery's OM-74 makes
// the bodies refuse unknown fields): `role: "Owner"` with no modules is a Global Admin, who holds
// every app by tier; `role: "Member"` holds exactly the modules, at least one.
//
// ⚠ NOTHING IS PRE-CHECKED, Donations included. A draft starts with no app chosen and no Global
// Admin, and `choiceBody` answers `null` until the choice is one the server would take, so the act's
// button stays disabled ("they shouldn't be able to be approved if nothing is checked", 22:54).
//
// ⚠ EVERY APP ON THE COMPANY'S LIST IS OFFERED, ONE THAT IS OFF INCLUDED (D-EP-76 (2), the console's
// own rule). `GET /admin/company/module-choices` lists every app the company could give, each with
// whether it is on, and both doors take an app that is off: the grant waits for it to be turned on
// (the backend's `org_module_choices`, "reported, never filtered"). So an off app is listed, marked
// "Off for the company" and still choosable. Only an app NOT on the list is one the company cannot
// give, and the server refuses it (`*_MODULE_UNAVAILABLE`). The console's access-request approval
// lists only the apps THIS BUILD SERVES (EP-WEB55 (c), `servedAppChoices`): an app with no page
// anywhere there is not offered, and an approval grants without taking anything away. The old
// employer portal's approval keeps the whole list (D-EP-86); and a demotion through a door naming no
// `shown_modules` must, since it replaces every app the company can see and a hidden one would be
// removed unseen.

import { isGivmoError, firstErrorCode, firstErrorContext } from '@givmo/api-client';
import { productLabel } from '../../lib/app-names';
import { ROLE_MEMBER, ROLE_OWNER } from './admins-roles';

/** A tier a person holds in one app, as the wire spells it. */
export type AppTier = typeof ROLE_MEMBER | typeof ROLE_OWNER;

/** One app the company could give, as `module-choices` serves it. */
export interface AppChoiceRow {
  module: string;
  on: boolean;
  /** The tiers the app offers, least first. */
  tiers: readonly AppTier[];
}

/** One app's place in a draft. */
export interface AppDraft {
  module: string;
  /** Whether the company has it on. An off app is still choosable; the picker marks it. */
  on: boolean;
  chosen: boolean;
  tier: AppTier;
  /** The levels this app offers, least first. */
  tiers: readonly AppTier[];
}

/** What the person making the choice has picked so far. */
export interface AppChoiceDraft {
  /** Make them a Global Admin. Ignores every app while set. */
  globalAdmin: boolean;
  /** Every app on the company's list, on or off, in the server's order. */
  apps: readonly AppDraft[];
}

/** The body both doors take, and nothing else. */
export interface AppChoiceBody {
  role: AppTier;
  modules: { module: string; role: AppTier }[];
}

const ALL_TIERS: readonly AppTier[] = [ROLE_MEMBER, ROLE_OWNER];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Tolerantly read the `module-choices` rows: a row without a string `module` is dropped, `on` is
 * true only when the row says so, and `roles` narrows the two tiers (absent or unreadable: both).
 */
export function decodeAppChoices(rows: readonly unknown[]): AppChoiceRow[] {
  const out: AppChoiceRow[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    if (!isRecord(row)) continue;
    const module = typeof row.module === 'string' ? row.module.trim() : '';
    if (module.length === 0 || seen.has(module)) continue;
    seen.add(module);
    const roles = Array.isArray(row.roles) ? row.roles : null;
    const tiers = roles === null ? ALL_TIERS : ALL_TIERS.filter((tier) => roles.includes(tier));
    out.push({ module, on: row.on === true, tiers: tiers.length > 0 ? tiers : [ROLE_MEMBER] });
  }
  return out;
}

/**
 * The rows the console's access-request approval offers (EP-WEB55 (c)): only the apps its build
 * serves, the set its own app grid draws (`servedModuleSlugs` in `@givmo/org-console`, Org
 * Machinery's OM-95), so an app with no page anywhere there is never offered. A served app that is off
 * stays on the list, marked and still choosable: D-EP-76 (2) and D-CPO-77 item 5 compose (Org
 * Machinery's reading, agreed 2026-10-24). The host hands the set in; with none (the old employer
 * portal, D-EP-86), every row, as before. ⚠ Never a demotion through a door that names no
 * `shown_modules`: that door replaces every app the company can see, so a row this list left out
 * would be removed unseen.
 */
export function servedAppChoices(
  rows: readonly AppChoiceRow[],
  served: readonly string[] | undefined,
): readonly AppChoiceRow[] {
  if (served === undefined) return rows;
  return rows.filter((row) => served.includes(row.module));
}

/** A fresh draft over every app on the list: nothing chosen, every level Member, no Global Admin. */
export function emptyAppChoice(rows: readonly AppChoiceRow[]): AppChoiceDraft {
  return {
    globalAdmin: false,
    apps: rows.map((row) => ({
      module: row.module,
      on: row.on,
      chosen: false,
      tier: ROLE_MEMBER,
      tiers: row.tiers,
    })),
  };
}

/**
 * The draft over a list that arrived (or changed) after it was opened: what was chosen survives, an
 * app no longer on the list drops out, a new one joins unchosen, and each app's on or off is the
 * list's. The person's work is never reset by a list that answers late.
 */
export function reseatAppChoice(
  draft: AppChoiceDraft,
  rows: readonly AppChoiceRow[],
): AppChoiceDraft {
  const before = new Map(draft.apps.map((app) => [app.module, app]));
  return {
    globalAdmin: draft.globalAdmin,
    apps: rows.map((row) => {
      const held = before.get(row.module);
      const tier = held !== undefined && row.tiers.includes(held.tier) ? held.tier : ROLE_MEMBER;
      return {
        module: row.module,
        on: row.on,
        chosen: held?.chosen ?? false,
        tier,
        tiers: row.tiers,
      };
    }),
  };
}

/** The draft with one app's choice or level changed. */
export function withApp(
  draft: AppChoiceDraft,
  module: string,
  next: Partial<Pick<AppDraft, 'chosen' | 'tier'>>,
): AppChoiceDraft {
  return {
    ...draft,
    apps: draft.apps.map((app) => (app.module === module ? { ...app, ...next } : app)),
  };
}

/**
 * The body for the choice, or `null` while it is not one to send: a Global Admin carries no apps;
 * anyone else carries exactly the chosen apps, at least one. `allowGlobalAdmin: false` (a demotion,
 * which by definition makes them a regular admin) ignores the Global Admin switch.
 */
export function choiceBody(
  draft: AppChoiceDraft,
  { allowGlobalAdmin = true }: { allowGlobalAdmin?: boolean } = {},
): AppChoiceBody | null {
  if (allowGlobalAdmin && draft.globalAdmin) return { role: ROLE_OWNER, modules: [] };
  const modules = draft.apps
    .filter((app) => app.chosen)
    .map((app) => ({ module: app.module, role: app.tier }));
  return modules.length > 0 ? { role: ROLE_MEMBER, modules } : null;
}

/** What the requester said they need, against what is on offer. */
export type RequestedAppState =
  | { kind: 'none' }
  | { kind: 'on'; label: string }
  | { kind: 'off'; label: string }
  | { kind: 'unavailable'; label: string };

/**
 * The requester's own note (`requested_app`), never a pre-checked box: an app that is on, an app the
 * company has but has turned off, or one it cannot give. `rows === null` (the list not read yet, or
 * not readable) says only what they asked for.
 */
export function requestedAppState(
  requestedApp: string | null | undefined,
  rows: readonly AppChoiceRow[] | null,
): RequestedAppState {
  const slug = (requestedApp ?? '').trim();
  if (slug.length === 0) return { kind: 'none' };
  const label = productLabel(slug);
  if (rows === null) return { kind: 'on', label };
  const row = rows.find((candidate) => candidate.module === slug);
  if (row === undefined) return { kind: 'unavailable', label };
  return row.on ? { kind: 'on', label } : { kind: 'off', label };
}

/** The doors' typed refusals, read off the CODE, never the server's sentence. */
export type ChoiceRefusal =
  | { kind: 'choice-required' }
  | { kind: 'modules-with-global-admin' }
  /** `module` is the server's slug, `null` when it names none; `label` its name. */
  | { kind: 'module-unavailable'; module: string | null; label: string }
  | { kind: 'duplicate-module'; label: string }
  | { kind: 'not-global-admin' }
  | { kind: 'last-global-admin' }
  | { kind: 'not-found' };

/** Each door's code prefix: the join approval's and the role change's. */
export type ChoiceDoor = 'JOIN_APPROVAL' | 'ROLE_CHANGE';

/**
 * Read a refusal of the choice: the four 422 codes of the door, the role change's 409
 * `ROLE_CHANGE_NOT_GLOBAL_ADMIN`, and a 404. A 404 is a backend older than OM-57, which has no such
 * route, or, on the join door, a request gone since the list loaded; either way nothing was written,
 * and the act is never retried on the old route. The role change answers an admin gone since the list
 * loaded with the role edit's own bare 400 ("… is not an admin of the company"), which reads as
 * `null` here, as the console's reader does. Anything else is `null`.
 *
 * ⚠ A BARE 400 IS THE LAST GLOBAL ADMIN ONLY ON A SELF-DEMOTION (`demotingSelf`), the console's
 * reading of the same writer (`@givmo/org-console`'s `roleChangeRefusal`). The route is Global-Admin
 * only (`company_admin:edit`), so a person demoting someone else is a Global Admin themselves, and
 * "the only one" cannot be true of them; the guard is the class writer's own 400, with no code.
 */
export function readChoiceRefusal(
  error: unknown,
  door: ChoiceDoor,
  { demotingSelf = false }: { demotingSelf?: boolean } = {},
): ChoiceRefusal | null {
  if (!isGivmoError(error)) return null;
  if (error.status === 404) return { kind: 'not-found' };
  const code = firstErrorCode(error);
  const context = firstErrorContext(error);
  const module = typeof context?.module === 'string' ? context.module.trim() : '';
  switch (code) {
    case `${door}_CHOICE_REQUIRED`:
      return { kind: 'choice-required' };
    case `${door}_MODULES_WITH_GLOBAL_ADMIN`:
      return { kind: 'modules-with-global-admin' };
    case `${door}_MODULE_UNAVAILABLE`:
      return {
        kind: 'module-unavailable',
        module: module.length > 0 ? module : null,
        label: productLabel(module),
      };
    case `${door}_DUPLICATE_MODULE`:
      return { kind: 'duplicate-module', label: productLabel(module) };
    default:
      break;
  }
  if (door === 'ROLE_CHANGE' && code === 'ROLE_CHANGE_NOT_GLOBAL_ADMIN') {
    return { kind: 'not-global-admin' };
  }
  if (door === 'ROLE_CHANGE' && demotingSelf && error.status === 400 && code === null) {
    return { kind: 'last-global-admin' };
  }
  return null;
}
